import React from 'react'
import { useTranslate } from 'src/localization/useTranslate'

export type TranslateNodeExpressions = Record<
  string,
  string | React.ReactElement
>

const TranslateNode: React.FC<{
  source: string
  expressions?: TranslateNodeExpressions
}> = ({ source, expressions }) => {
  const translate = useTranslate()
  return <>{translate(source, expressions ?? {})}</>
}

/**
 * Deferred translation for code that runs **outside a component render** —
 * model getters, validators, redux action payloads, and module-scope helpers,
 * where hooks cannot be called.
 *
 * Returns a small element that translates itself when rendered, so the locale
 * and white-label brand are read (from ConfigContext) at render time, not at
 * call time — a label created while building a model still re-translates
 * correctly when it is eventually rendered. The returned element exposes
 * `props.source` for callers that need the untranslated English source
 * (e.g. analytics).
 *
 * **Inside function components, prefer `useTranslate()`** — it returns plain
 * strings for string-only expressions, which compose better (aria-labels,
 * string props, concatenation) and avoid an extra component per string.
 * Reach for `translateNode()` only when there is no component scope to hook
 * into.
 */
export const translateNode = (
  source: string,
  expressions?: TranslateNodeExpressions,
): React.ReactElement => (
  <TranslateNode source={source} expressions={expressions} />
)
